import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import { CustomDropdown } from '@/components/ui/aawaz-dropdown';
import { useToast } from '@/hooks/use-toast';
import { Settings, Loader2, ArrowUp, Save, Key, Globe, ChevronsLeftRight, ChevronsRightLeft, Check, Search, RefreshCw } from 'lucide-react';
import type { VoiceConfig, Message } from './TextToSpeechApp';
import { useIsDarkMode } from '@/hooks/useIsDarkMode';
import { cn } from '@/lib/utils';
import { generateTextToSpeech, generateSarvamSpeech } from '@/services/textToSpeechService';
import { Switch } from '@/components/ui/switch';

const SARVAM_LANGUAGES = [
  { value: 'en-IN', label: '🇬🇧 English (Indian)' },
  { value: 'hi-IN', label: '🇮🇳 Hindi' },
  { value: 'bn-IN', label: '🇮🇳 Bengali' },
  { value: 'ta-IN', label: '🇮🇳 Tamil' },
  { value: 'te-IN', label: '🇮🇳 Telugu' },
  { value: 'gu-IN', label: '🇮🇳 Gujarati' },
  { value: 'kn-IN', label: '🇮🇳 Kannada' },
  { value: 'ml-IN', label: '🇮🇳 Malayalam' },
  { value: 'mr-IN', label: '🇮🇳 Marathi' },
  { value: 'pa-IN', label: '🇮🇳 Punjabi' },
  { value: 'od-IN', label: '🇮🇳 Odia' }
];

const SARVAM_SPEAKERS = [
  { value: 'shubh', label: 'Shubh', category: 'Default' },
  { value: 'aditya', label: 'Aditya', category: 'Male' },
  { value: 'ritu', label: 'Ritu', category: 'Female' },
  { value: 'priya', label: 'Priya', category: 'Female' },
  { value: 'neha', label: 'Neha', category: 'Female' },
  { value: 'rahul', label: 'Rahul', category: 'Male' },
  { value: 'pooja', label: 'Pooja', category: 'Female' },
  { value: 'rohan', label: 'Rohan', category: 'Male' },
  { value: 'simran', label: 'Simran', category: 'Female' },
  { value: 'kavya', label: 'Kavya', category: 'Female' },
  { value: 'amit', label: 'Amit', category: 'Male' },
  { value: 'dev', label: 'Dev', category: 'Male' },
  { value: 'ishita', label: 'Ishita', category: 'Female' },
  { value: 'shreya', label: 'Shreya', category: 'Female' },
  { value: 'ratan', label: 'Ratan', category: 'Male' },
  { value: 'varun', label: 'Varun', category: 'Male' },
  { value: 'manan', label: 'Manan', category: 'Male' },
  { value: 'sumit', label: 'Sumit', category: 'Male' },
  { value: 'roopa', label: 'Roopa', category: 'Female' },
  { value: 'kabir', label: 'Kabir', category: 'Male' },
  { value: 'aayan', label: 'Aayan', category: 'Male' },
  { value: 'ashutosh', label: 'Ashutosh', category: 'Male' },
  { value: 'advait', label: 'Advait', category: 'Male' },
  { value: 'anand', label: 'Anand', category: 'Male' },
  { value: 'tanya', label: 'Tanya', category: 'Female' },
  { value: 'tarun', label: 'Tarun', category: 'Male' },
  { value: 'sunny', label: 'Sunny', category: 'Male' },
  { value: 'mani', label: 'Mani', category: 'Male' },
  { value: 'gokul', label: 'Gokul', category: 'Male' },
  { value: 'vijay', label: 'Vijay', category: 'Male' },
  { value: 'shruti', label: 'Shruti', category: 'Female' },
  { value: 'suhani', label: 'Suhani', category: 'Female' },
  { value: 'mohit', label: 'Mohit', category: 'Male' },
  { value: 'kavitha', label: 'Kavitha', category: 'Female' },
  { value: 'rehan', label: 'Rehan', category: 'Male' },
  { value: 'soham', label: 'Soham', category: 'Male' },
  { value: 'rupali', label: 'Rupali', category: 'Female' }
];

const SARVAM_LANGUAGE_OPTIONS = SARVAM_LANGUAGES.map(lang => ({
  value: lang.value,
  label: lang.label,
  category: 'Indian Languages'
}));

const SARVAM_SPEAKER_OPTIONS = SARVAM_SPEAKERS.map(speaker => ({
  value: speaker.value,
  label: speaker.label,
  category: speaker.category
}));

const VOICE_CATEGORIES = {
  '🇺🇸 American Female': ['Autumn', 'Melody', 'Hannah', 'Emily', 'Ivy', 'Kaitlyn', 'Luna', 'Willow', 'Lauren', 'Sierra'],
  '🇺🇸 American Male': ['Noah', 'Jasper', 'Caleb', 'Ronan', 'Ethan', 'Daniel', 'Zane'],
  '🇨🇳 Chinese Female': ['Mei', 'Lian', 'Ting', 'Jing'],
  '🇨🇳 Chinese Male': ['Wei', 'Jian', 'Hao', 'Sheng'],
  '🇪🇸 Spanish': ['Lucía', 'Mateo', 'Javier'],
  '🇫🇷 French': ['Élodie'],
  '🇮🇳 Hindi': ['Ananya', 'Priya', 'Arjun', 'Rohan'],
  '🇮🇹 Italian': ['Giulia', 'Luca'],
  '🇵🇹 Portuguese': ['Camila', 'Thiago', 'Rafael']
};

const ENDPOINTS = {
  speech: { label: '2K chars', limit: 2000, desc: 'Standard, ~4s' },
  synthesisTasks: { label: '200K chars', limit: 500000, desc: 'Books, ~15min' }
};

const VOICE_OPTIONS = Object.entries(VOICE_CATEGORIES).flatMap(([category, voices]) =>
  voices.map(voice => ({
    value: voice,
    label: voice,
    category: category
  }))
);

const ENDPOINT_OPTIONS = Object.entries(ENDPOINTS).map(([key, config]) => ({
  value: key,
  label: config.label,
  description: config.desc
}));

interface UniversalInputProps {
  voiceConfig: VoiceConfig;
  onVoiceConfigChange: (config: VoiceConfig) => void;
  onSendMessage: (text: string) => Message;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  onUpdateMessage: (
    messageId: string, 
    audioUrl: string, 
    timestampsUrl?: string, 
    audioResults?: Array<{ voice: string; audioUrl: string; timestampsUrl?: string }>
  ) => void;
}

const verticalFadeStyle: React.CSSProperties = {
  WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
  maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
};

export function UniversalInput({
  voiceConfig,
  onVoiceConfigChange,
  onSendMessage,
  isLoading,
  setIsLoading,
  onUpdateMessage
}: UniversalInputProps) {
  const [text, setText] = useState('');
  const [customSarvamKey, setCustomSarvamKey] = useState(() => localStorage.getItem('custom_sarvam_api_key') || '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();
  const isDarkMode = useIsDarkMode();

  const [isComposerVisible, setIsComposerVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const [speakerSearchQuery, setSpeakerSearchQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setIsComposerVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overscrollBehaviorY = 'contain';
    } else {
      document.body.style.overscrollBehaviorY = '';
    }
    return () => {
      document.body.style.overscrollBehaviorY = '';
    };
  }, [isExpanded]);

  useEffect(() => {
    if (!textareaRef.current) return;

    textareaRef.current.style.transition = 'none';
    textareaRef.current.style.height = 'auto';
    const scrollHeight = textareaRef.current.scrollHeight;

    if (scrollHeight > 224) {
      setShowExpandButton(true);
    } else {
      setShowExpandButton(false);
      if (isExpanded) {
        setIsExpanded(false);
      }
    }

    if (isExpanded) {
      textareaRef.current.style.transition = 'height 0.3s ease-in-out';
      textareaRef.current.style.height = '80vh';
    } else {
      const defaultHeight = Math.max(36, scrollHeight);
      textareaRef.current.style.height = `${defaultHeight}px`;
    }
  }, [text, isExpanded]);

  useEffect(() => {
    if (!text && isExpanded) {
      setIsExpanded(false);
    }
  }, [text, isExpanded]);

  const saveCustomSarvamKey = () => {
    const trimmed = customSarvamKey.trim();
    if (trimmed) {
      localStorage.setItem('custom_sarvam_api_key', trimmed);
      toast({ title: 'Custom API key saved', description: 'Your Sarvam AI key will be used for generation.' });
    } else {
      localStorage.removeItem('custom_sarvam_api_key');
      toast({ title: 'Custom API key cleared', description: 'Falling back to system keys.' });
    }
  };

  const selectedEndpoint = ENDPOINTS[voiceConfig.endpoint as keyof typeof ENDPOINTS];
  const characterCount = text.length;
  const selectedLimit = voiceConfig.useSarvam ? 2500 : (selectedEndpoint?.limit || 2000);
  const isOverLimit = characterCount > selectedLimit;

  const toggleVoiceSelection = (voiceName: string) => {
    const currentVoices = voiceConfig.voices || [voiceConfig.voice];
    let nextVoices: string[];
    if (currentVoices.includes(voiceName)) {
      if (currentVoices.length <= 1) {
        toast({
          title: "Selection required",
          description: "Please keep at least one voice model selected.",
          variant: "destructive"
        });
        return;
      }
      nextVoices = currentVoices.filter(v => v !== voiceName);
    } else {
      nextVoices = [...currentVoices, voiceName];
    }
    onVoiceConfigChange({
      ...voiceConfig,
      voices: nextVoices,
      voice: nextVoices[0]
    });
  };

  const toggleSarvamSpeakerSelection = (speakerName: string) => {
    const currentSpeakers = voiceConfig.sarvamSpeakers || [voiceConfig.sarvamSpeaker || 'shubh'];
    let nextSpeakers: string[];
    if (currentSpeakers.includes(speakerName)) {
      if (currentSpeakers.length <= 1) {
        toast({
          title: "Selection required",
          description: "Please keep at least one speaker selected.",
          variant: "destructive"
        });
        return;
      }
      nextSpeakers = currentSpeakers.filter(s => s !== speakerName);
    } else {
      nextSpeakers = [...currentSpeakers, speakerName];
    }
    onVoiceConfigChange({
      ...voiceConfig,
      sarvamSpeakers: nextSpeakers,
      sarvamSpeaker: nextSpeakers[0]
    });
  };

  const generateSpeech = async () => {
    if (!text.trim()) {
      toast({
        title: "No text provided",
        description: "Please enter some text to convert to speech.",
        variant: "destructive"
      });
      return;
    }

    if (isOverLimit) {
      toast({
        title: "Text too long",
        description: `Please limit text to ${selectedLimit.toLocaleString()} characters.`,
        variant: "destructive"
      });
      return;
    }

    const message = onSendMessage(text);
    setText('');
    setIsExpanded(false);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    setIsLoading(true);

    try {
      if (voiceConfig.useSarvam) {
        const speakers = voiceConfig.sarvamSpeakers && voiceConfig.sarvamSpeakers.length > 0
          ? voiceConfig.sarvamSpeakers
          : [voiceConfig.sarvamSpeaker || 'shubh'];

        const results = await Promise.all(
          speakers.map(async (speaker) => {
            try {
              const data = await generateSarvamSpeech({
                text,
                speaker,
                languageCode: voiceConfig.sarvamLanguage || 'en-IN',
                pace: voiceConfig.sarvamPace ?? 1.0,
              });
              if (data.success && data.audioContent) {
                return {
                  voice: speaker,
                  audioUrl: `data:audio/wav;base64,${data.audioContent}`,
                };
              }
              return null;
            } catch (err) {
              console.error(`Failed to generate Sarvam speech for speaker ${speaker}:`, err);
              return null;
            }
          })
        );

        const validResults = results.filter((r): r is { voice: string; audioUrl: string } => r !== null);
        if (validResults.length === 0) {
          throw new Error('Failed to generate speech for any of the selected speakers.');
        }

        onUpdateMessage(message.id, validResults[0].audioUrl, undefined, validResults);
      } else {
        const voices = voiceConfig.voices && voiceConfig.voices.length > 0
          ? voiceConfig.voices
          : [voiceConfig.voice];

        const results = await Promise.all(
          voices.map(async (voice) => {
            try {
              const data = await generateTextToSpeech({
                text,
                voiceId: voice,
                speed: voiceConfig.speed,
                pitch: voiceConfig.pitch,
                endpoint: voiceConfig.endpoint,
                timestampType: 'word'
              });
              if (data.success) {
                let audioSrc = '';
                if (data.audioContent) {
                  audioSrc = `data:audio/mp3;base64,${data.audioContent}`;
                } else if (data.OutputUri) {
                  audioSrc = data.OutputUri;
                }
                return {
                  voice,
                  audioUrl: audioSrc,
                  timestampsUrl: data.TimestampsUri
                };
              }
              return null;
            } catch (err) {
              console.error(`Failed to generate UnrealSpeech for voice ${voice}:`, err);
              return null;
            }
          })
        );

        const validResults = results.filter((r): r is NonNullable<typeof r> => r !== null);
        if (validResults.length === 0) {
          throw new Error('Failed to generate speech for any of the selected voices.');
        }

        onUpdateMessage(message.id, validResults[0].audioUrl, validResults[0].timestampsUrl, validResults);
      }
    } catch (error: any) {
      console.error('Speech generation failed:', error);
      toast({
        title: "Speech generation failed",
        description: error.message || 'An error occurred while generating speech.',
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderSlider = (
    label: string,
    value: number,
    onChange: (val: number) => void,
    min: number,
    max: number,
    step: number,
    displayValue: string
  ) => (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center px-0.5">
        <label className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">{label}</label>
        <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">{displayValue}</span>
      </div>
      <div className="flex items-center gap-3 bg-background-alt/50 border border-border/60 p-2 rounded-2xl backdrop-blur-sm">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 rounded-xl p-0 font-extrabold hover:bg-accent hover:text-accent-foreground shrink-0 text-sm select-none"
          onClick={() => onChange(Math.max(min, Number((value - step).toFixed(1))))}
        >
          －
        </Button>
        <Slider
          value={[value]}
          onValueChange={(val) => onChange(val[0])}
          min={min}
          max={max}
          step={step}
          className="flex-1 cursor-pointer"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 rounded-xl p-0 font-extrabold hover:bg-accent hover:text-accent-foreground shrink-0 text-sm select-none"
          onClick={() => onChange(Math.min(max, Number((value + step).toFixed(1))))}
        >
          ＋
        </Button>
      </div>
    </div>
  );

  const renderSettingsContent = () => {
    const isSarvam = voiceConfig.useSarvam;
    const selectedVoices = voiceConfig.voices || [voiceConfig.voice];
    const selectedSpeakers = voiceConfig.sarvamSpeakers || [voiceConfig.sarvamSpeaker || 'shubh'];

    const sarvamCategorized = {
      'Default': ['shubh'],
      'Female Speakers': ['ritu', 'priya', 'neha', 'pooja', 'simran', 'kavya', 'ishita', 'shreya', 'roopa', 'tanya', 'shruti', 'suhani', 'kavitha', 'rupali'],
      'Male Speakers': ['aditya', 'rahul', 'rohan', 'amit', 'dev', 'ratan', 'varun', 'manan', 'sumit', 'kabir', 'aayan', 'ashutosh', 'advait', 'anand', 'tarun', 'sunny', 'mani', 'gokul', 'vijay', 'mohit', 'rehan', 'soham']
    };

    return (
      <div className="space-y-5 p-4 max-h-[85vh] overflow-y-auto md:max-h-none md:p-1">
        <div className="flex items-center justify-between border-b border-border/40 pb-3 md:hidden">
          <h2 className="text-base font-bold text-foreground flex items-center gap-2">
            <Settings className="w-4 h-4 text-primary animate-pulse" />
            Voice Configuration
          </h2>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 text-xs rounded-lg px-2 text-muted-foreground hover:text-foreground"
            onClick={() => {
              if (isSarvam) {
                onVoiceConfigChange({ ...voiceConfig, sarvamSpeakers: ['shubh'], sarvamSpeaker: 'shubh' });
              } else {
                onVoiceConfigChange({ ...voiceConfig, voices: ['Sierra'], voice: 'Sierra' });
              }
            }}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            Reset
          </Button>
        </div>


        <div className="space-y-1">
          {isSarvam ? (
            <>
              {/* Language Selection */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">Target Language</label>
                <CustomDropdown
                  value={voiceConfig.sarvamLanguage || 'en-IN'}
                  onValueChange={(sarvamLanguage) => onVoiceConfigChange({ ...voiceConfig, sarvamLanguage })}
                  options={SARVAM_LANGUAGE_OPTIONS}
                  placeholder="Select Language"
                  className="w-full"
                  buttonClassName="bg-background/40 backdrop-blur-sm border-border rounded-xl h-9 text-xs"
                  contentClassName="bg-popover border-border max-h-48 rounded-xl"
                />
              </div>

              {/* Multi Speaker Selection */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground tracking-wider uppercase block">
                  Select Speakers
                </label>

                {/* Categorized Speaker Selection Grid */}
                <div className="space-y-3.5 max-h-52 overflow-y-auto p-2 border border-[#1c1c1c] rounded-xl bg-background/25">
                  {Object.entries(sarvamCategorized).map(([category, speakers]) => {
                    const filtered = speakers.filter(sName => {
                      const spObj = SARVAM_SPEAKERS.find(x => x.value === sName);
                      return spObj && spObj.label.toLowerCase().includes(speakerSearchQuery.toLowerCase());
                    });
                    if (filtered.length === 0) return null;

                    return (
                      <div key={category} className="space-y-1">
                        <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider px-1">
                          {category}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                          {filtered.map(speakerName => {
                            const active = selectedSpeakers.includes(speakerName);
                            const label = SARVAM_SPEAKERS.find(x => x.value === speakerName)?.label || speakerName;
                            return (
                              <button
                                key={speakerName}
                                type="button"
                                onClick={() => toggleSarvamSpeakerSelection(speakerName)}
                                className={cn(
                                  "flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium border text-left transition-all duration-200 select-none",
                                  active
                                    ? "bg-primary/10 border-primary text-primary shadow-sm scale-[0.98]"
                                    : "bg-background/40 hover:bg-accent/40 border-border/60 text-muted-foreground hover:text-foreground"
                                )}
                              >
                                <span className="truncate">{label}</span>
                                {active && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* API Key settings */}
              <div className="space-y-1.5 pt-2.5 border-t border-border/30">
                <label className="text-xs font-semibold text-muted-foreground tracking-wider uppercase flex items-center gap-1.5">
                  <Key className="h-3.5 w-3.5 text-primary" /> 
                  Custom API Key
                </label>
                <div className="flex gap-1.5">
                  <Input
                    type="password"
                    placeholder="Enter sk_..."
                    value={customSarvamKey}
                    onChange={(e) => setCustomSarvamKey(e.target.value)}
                    className="flex-1 text-xs h-8 rounded-lg"
                  />
                  <Button size="sm" onClick={saveCustomSarvamKey} className="h-8 px-2.5 shrink-0 rounded-lg">
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Pace Slider */}
              {renderSlider(
                "Pace",
                voiceConfig.sarvamPace || 1.0,
                (val) => onVoiceConfigChange({ ...voiceConfig, sarvamPace: val }),
                0.5,
                2.0,
                0.1,
                `${voiceConfig.sarvamPace || 1.0}x`
              )}
            </>
          ) : (
            <>
              {/* Default UnrealSpeech Model Selection */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground tracking-wider uppercase block">
                  Select Voice Models
                </label>

                <div className="space-y-3.5 max-h-52 overflow-y-auto p-2 border border-border/50 rounded-xl bg-background/25">
                  {Object.entries(VOICE_CATEGORIES).map(([category, voices]) => {
                    const filtered = voices.filter(v => v.toLowerCase().includes(speakerSearchQuery.toLowerCase()));
                    if (filtered.length === 0) return null;

                    return (
                      <div key={category} className="space-y-1">
                        <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide px-1">
                          {category}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                          {filtered.map(voiceName => {
                            const active = selectedVoices.includes(voiceName);
                            return (
                              <button
                                key={voiceName}
                                type="button"
                                onClick={() => toggleVoiceSelection(voiceName)}
                                className={cn(
                                  "flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium border text-left transition-all duration-200 select-none",
                                  active
                                    ? "bg-primary/10 border-primary text-primary shadow-sm scale-[0.98]"
                                    : "bg-background/40 hover:bg-accent/40 border-border/60 text-muted-foreground hover:text-foreground"
                                )}
                              >
                                <span className="truncate">{voiceName}</span>
                                {active && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Endpoint selection */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground tracking-wider uppercase">Character Limit</label>
                <CustomDropdown
                  value={voiceConfig.endpoint}
                  onValueChange={(endpoint) => onVoiceConfigChange({ ...voiceConfig, endpoint })}
                  options={ENDPOINT_OPTIONS}
                  placeholder="Select Type"
                  className="w-full"
                  buttonClassName="bg-background/40 backdrop-blur-sm border-border rounded-xl h-9 text-xs"
                  contentClassName="bg-popover border-border rounded-xl"
                />
              </div>

              {/* Tone Properties: Speed */}
              {renderSlider(
                "Speed",
                voiceConfig.speed,
                (val) => onVoiceConfigChange({ ...voiceConfig, speed: val }),
                -1,
                1,
                0.1,
                voiceConfig.speed > 0 ? `+${voiceConfig.speed}` : `${voiceConfig.speed}`
              )}

              {/* Tone Properties: Pitch */}
              {renderSlider(
                "Pitch",
                voiceConfig.pitch,
                (val) => onVoiceConfigChange({ ...voiceConfig, pitch: val }),
                0.5,
                1.5,
                0.1,
                `${voiceConfig.pitch}`
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 p-2 pb-1 z-20 transition-all duration-500 ease-out",
        isComposerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
      )}
    >
      <div className="max-w-2xl mx-auto">
        <div className="relative flex flex-col">
          <div
            className={cn(
              "w-full flex flex-col border transition-[border-radius,box-shadow,background-color,border-color] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-[border-radius] rounded-[28px]",
              isDarkMode
                ? "bg-[rgba(37,37,37,0.6)] backdrop-blur-[22px] backdrop-saturate-200 border-white/15 ring-0 shadow-none"
                : "bg-white/70 backdrop-blur-[22px] backdrop-saturate-200 border-black/10 ring-0 shadow-none"
            )}
          >
            <div
              className={cn(
                "flex w-full gap-2 transition-[padding] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]",
                "items-start px-4 pt-1.5 relative"
              )}
            >
              <div className="flex-1 min-w-0" style={!isExpanded ? verticalFadeStyle : undefined}>
                <Textarea
                  ref={textareaRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      generateSpeech();
                    }
                  }}
                  placeholder="Message Text to Speech"
                  disabled={isLoading}
                  className={cn(
                    "w-full resize-none bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm leading-relaxed box-border overflow-y-auto",
                    "py-2 px-0 pr-10 min-h-[36px]",
                    isExpanded ? "max-h-[80vh] overflow-y-auto" : "max-h-56",
                    isDarkMode ? "text-white placeholder:text-white/40" : "text-gray-900 placeholder:text-gray-400"
                  )}
                />
              </div>

              {showExpandButton && (
                <button
                  type="button"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className={cn(
                    "absolute right-2 top-2 p-1.5 rounded-full backdrop-blur-sm transition-all z-20",
                    isDarkMode
                      ? "bg-black/20 hover:bg-black/40 text-gray-400 hover:text-white"
                      : "bg-white/20 hover:bg-white/40 text-gray-500 hover:text-gray-900"
                  )}
                  title={isExpanded ? "Collapse" : "Expand"}
                >
                  {isExpanded ? (
                    <ChevronsRightLeft className="w-4 h-4" style={{ transform: 'rotate(45deg)' }} />
                  ) : (
                    <ChevronsLeftRight className="w-4 h-4" style={{ transform: 'rotate(45deg)' }} />
                  )}
                </button>
              )}
            </div>

            <div className={cn("pt-2 px-3 pb-2 flex items-center justify-between")}>
              <div className="flex items-center gap-2">
                <div className={cn(
                  "flex items-center rounded-full p-0.5 relative group",
                  isDarkMode ? "bg-white/10" : "bg-black/5"
                )}>
                  {/* Custom Tooltip */}
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 hidden md:group-hover:flex items-center justify-center bg-black dark:bg-white text-white dark:text-black text-xs px-2 py-1 rounded whitespace-nowrap shadow-lg z-50 transition-opacity">
                    Select Voice Model
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-black dark:bg-white rotate-45"></div>
                  </div>

                  <button
                    type="button"
                    onClick={() => onVoiceConfigChange({ ...voiceConfig, useSarvam: false })}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200",
                      !voiceConfig.useSarvam
                        ? (isDarkMode ? "bg-[#303030] text-white shadow-sm" : "bg-white text-black shadow-sm")
                        : (isDarkMode ? "text-white/60 hover:text-white" : "text-black/60 hover:text-black")
                    )}
                  >
                    <Globe className="w-3.5 h-3.5" />
                    {!voiceConfig.useSarvam && <span>Default</span>}
                  </button>

                  <button
                    type="button"
                    onClick={() => onVoiceConfigChange({ ...voiceConfig, useSarvam: true })}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200",
                      voiceConfig.useSarvam
                        ? (isDarkMode ? "bg-[#303030] text-white shadow-sm" : "bg-white text-black shadow-sm")
                        : (isDarkMode ? "text-white/60 hover:text-white" : "text-black/60 hover:text-black")
                    )}
                  >
                    <span className="text-sm leading-none">✨</span>
                    {voiceConfig.useSarvam && <span>Sarvam</span>}
                  </button>
                </div>
              </div>

              {/* Character count */}
              {text && (
                <div className="text-xs text-muted-foreground flex-1 text-center px-2 whitespace-nowrap overflow-hidden text-ellipsis">
                  {characterCount.toLocaleString()} / {selectedLimit.toLocaleString()} <span className="hidden sm:inline">characters</span>
                </div>
              )}

              <div className="flex items-center gap-2">
                {/* Settings Mobile */}
                <div className="md:hidden">
                  <Drawer>
                    <DrawerTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "inline-flex gap-1.5 items-center justify-center whitespace-nowrap text-sm focus-visible:outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:cursor-default data-[disabled=true]:opacity-50 group/button relative px-3 py-2 h-9 rounded-full max-w-52 overflow-hidden font-semibold outline-none disabled:opacity-50",
                          isDarkMode
                            ? "bg-white/10 hover:bg-white/15 text-white"
                            : "bg-black/[0.04] hover:bg-black/[0.08] text-gray-900"
                        )}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent
                      className={cn(
                        'h-[70vh] border-border transition-colors',
                        isDarkMode ? 'bg-background' : 'bg-white'
                      )}
                    >
                      {renderSettingsContent()}
                    </DrawerContent>
                  </Drawer>
                </div>

                {/* Settings Desktop */}
                <div className="hidden md:block">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "inline-flex gap-1.5 items-center justify-center whitespace-nowrap text-sm focus-visible:outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:cursor-default data-[disabled=true]:opacity-50 group/button relative px-3 py-2 h-9 rounded-full max-w-52 overflow-hidden font-semibold outline-none disabled:opacity-50",
                          isDarkMode
                            ? "bg-white/10 hover:bg-white/15 text-white"
                            : "bg-black/[0.04] hover:bg-black/[0.08] text-gray-900"
                        )}
                      >
                        <Settings className="w-4 h-4" />
                        <span className="hidden md:inline text-xs font-medium">Settings</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent
                      className={cn(
                        'rounded-none w-full max-w-md border-border transition-colors',
                        isDarkMode ? 'bg-background' : 'bg-white'
                      )}
                    >
                      <DialogTitle className="text-lg font-semibold text-center mb-4 text-foreground">Voice Settings</DialogTitle>
                      <DialogDescription className="sr-only">Configure voice settings for text to speech.</DialogDescription>
                      {renderSettingsContent()}
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Send Button */}
                <Button
                  onClick={generateSpeech}
                  disabled={isLoading || !text.trim() || isOverLimit}
                  className={cn(
                    "h-9 w-9 rounded-full p-0 flex items-center justify-center transition-all duration-300",
                    isDarkMode
                      ? "bg-white text-black hover:bg-gray-100"
                      : "bg-gray-900 text-white hover:bg-gray-800"
                  )}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ArrowUp className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
